/**
 * Sign in, and the two screens that hang off it when a password is forgotten.
 *
 * Signing in is two steps, because every account carries a second factor: email
 * and password first, then the six digit code. The second step keeps the
 * challenge id from the first, and a wrong code returns you to the code box
 * rather than the top.
 *
 * The forgotten-password path lives here rather than on pages of its own, and
 * that is a deliberate choice rather than a shortcut. This is the only screen
 * the app serves to somebody who is not signed in: it is the one route outside
 * the guard, it already knows how to redirect a person who turns out to be
 * signed in after all, and asking for a link and setting a new password both
 * end on this form anyway. Two more unauthenticated pages would be two more
 * places to keep that redirect correct, and the day one of them drifts is the
 * day a signed-out screen renders behind a session.
 *
 * So the page has four states and one job. `mode` decides which, and the URL
 * decides `mode` exactly once, on the first render:
 *
 *   sign-in   email and password
 *   code      the authenticator, once a challenge exists
 *   forgot    ask for a link
 *   reset     set a new password, reached from `/login?reset=<token>`
 *
 * Two things the server does that this file must not contradict:
 *
 *   - **A reset request answers the same whether or not the account exists.**
 *     There is no "no account with that address" state below, and adding one
 *     would hand back the enumeration answer the endpoint refuses to give. The
 *     confirmation says what was actually promised, which is weaker than "we
 *     sent you an email" and has to stay that way.
 *   - **A reset does not sign anybody in.** It returns no session, because an
 *     account with an authenticator still owes a code. Finishing one drops the
 *     person on the sign-in form with their address already filled in, which is
 *     the shortest honest path back.
 */
import type { FormEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { LogoLockup } from '../components/Logo'
import { Button } from '../components/Button'
import { Field } from '../components/Field'
import { Input } from '../components/Input'
import { PasswordInput } from '../components/PasswordInput'
import { ErrorState } from '../components/States'
import {
  clearSessionCache,
  useAdoptSession,
  useForgotPassword,
  useLogin,
  useResetPassword,
  useResetSubject,
  useSession,
  useVerifyTotp,
} from '../lib/session'
import { isMfaChallenge } from '../lib/types'
import { useDocumentTitle } from '../lib/hooks'

interface LocationState {
  from?: string
}

type Mode = 'signIn' | 'forgot' | 'reset'

const TITLES: Record<Mode, string> = {
  signIn: 'Sign in',
  forgot: 'Forgotten password',
  reset: 'Set a new password',
}

export default function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const destination = (location.state as LocationState | null)?.from ?? '/inbox'

  const [params, setParams] = useSearchParams()
  /**
   * The token is read once and then held in state.
   *
   * Held rather than re-read on every render because finishing a reset takes it
   * out of the URL, and a component that read the URL each time would lose the
   * token mid-submit. It stays in the address bar until then on purpose: a
   * reload of a half-filled form has to keep working, and a link that is single
   * use and expires within the hour is not made safer by being harder to reload.
   */
  const [resetToken] = useState<string | null>(() => params.get('reset'))
  const [mode, setMode] = useState<Mode>(() => (params.get('reset') === null ? 'signIn' : 'reset'))

  useDocumentTitle(TITLES[mode])

  const { session, isLoading } = useSession()
  const adoptSession = useAdoptSession()
  const login = useLogin()
  const verifyTotp = useVerifyTotp()
  const forgot = useForgotPassword()
  const resetPassword = useResetPassword()
  const resetSubject = useResetSubject(mode === 'reset' ? resetToken : null)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [challengeId, setChallengeId] = useState<string | null>(null)
  /** Set once a reset finishes, so the sign-in form can say why it is showing. */
  const [justReset, setJustReset] = useState(false)
  const codeRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (challengeId) codeRef.current?.focus()
  }, [challengeId])

  /**
   * Already signed in, so there is nothing to do on the sign-in form.
   *
   * The redirect is on the *mode*, not on the page, and the difference is the
   * whole scenario this feature exists for. Somebody signed in on their laptop
   * asks for a reset because they cannot remember the password on their phone.
   * Bounced to the dashboard, they land somewhere that offers no way to set a
   * password and no explanation of why the link did nothing, and nowhere is
   * literal: `setPassword` is reachable from no route in the product, so this
   * link is the only way anybody changes a password here.
   *
   * So the forgotten-password flow goes through, both halves of it. Guarding
   * only the reset screen was not enough: a dead link offers "send me a new
   * one", which moves to `forgot`, and that put the person straight back on the
   * dashboard from the button that was meant to rescue them.
   *
   * Setting a new password while signed in is coherent, and it ends this
   * session along with the others, which is what the screen and the email both
   * say it does.
   */
  if (!isLoading && session && mode === 'signIn') return <Navigate to={destination} replace />

  const passwordsMatch = newPassword !== '' && newPassword === confirmPassword
  const confirmTouched = confirmPassword !== ''

  const backToSignIn = () => {
    setMode('signIn')
    setPassword('')
    setCode('')
    setChallengeId(null)
    login.reset()
    verifyTotp.reset()
    forgot.reset()
  }

  const submitCredentials = (event: FormEvent) => {
    event.preventDefault()
    login.mutate(
      { email: email.trim(), password },
      {
        onSuccess: (result) => {
          if (isMfaChallenge(result)) {
            setChallengeId(result.challengeId)
            return
          }
          adoptSession(result)
          navigate(destination, { replace: true })
        },
      },
    )
  }

  const submitCode = (event: FormEvent) => {
    event.preventDefault()
    if (!challengeId) return
    verifyTotp.mutate(
      { challengeId, code: code.trim() },
      {
        onSuccess: () => navigate(destination, { replace: true }),
        onError: () => setCode(''),
      },
    )
  }

  const submitForgot = (event: FormEvent) => {
    event.preventDefault()
    forgot.mutate(email.trim())
  }

  const submitReset = (event: FormEvent) => {
    event.preventDefault()
    if (resetToken === null || !passwordsMatch) return
    resetPassword.mutate(
      { token: resetToken, password: newPassword },
      {
        onSuccess: () => {
          const account = resetSubject.data?.email ?? ''
          // Every session on the account is gone, including whichever one this
          // browser was holding. Dropping the cached copy is what stops the
          // guard above redirecting to a dashboard that is about to answer 401.
          clearSessionCache()
          // The token is spent, so it leaves the address bar. A reload from here
          // has to land on the sign-in form rather than on a refused link.
          setParams({}, { replace: true })
          setEmail(account)
          setNewPassword('')
          setConfirmPassword('')
          setJustReset(true)
          setMode('signIn')
        },
      },
    )
  }

  return (
    // Its own scroll area, a screen tall: the app locks the document's
    // scrolling, so a screen taller than the window (the two-factor setup with
    // its QR code on a short laptop) could not be scrolled. The inner column
    // keeps it centred when it fits.
    <main className="h-dvh overflow-y-auto bg-canvas">
      <div className="flex min-h-full items-center justify-center px-4 py-12">
      <div className="w-full max-w-[360px]">
        <div className="mb-6 flex flex-col items-start gap-1.5">
          {/* The kit's horizontal lockup, its default wherever the product names itself. */}
          <LogoLockup className="h-9 w-auto" />
          <p className="text-xs text-ink-subtle">Custom AI operator</p>
        </div>

        <div className="rounded-xl border border-line bg-surface p-6 shadow-xs sm:p-8">
          {mode === 'forgot' ? (
            forgot.isSuccess ? (
              <div className="space-y-5">
                <div className="space-y-2">
                  <h1 className="text-3xl font-semibold text-ink">Check your email</h1>
                  {/*
                    Says what was promised and nothing more. The server answers
                    the same for an address with no account, so "we have emailed
                    you" would be a claim this screen cannot make.
                  */}
                  <p className="text-base text-ink-muted">
                    If that address has an account, a link to set a new password is on its way to it. The
                    link works once and stops working after an hour.
                  </p>
                  <p className="text-base text-ink-muted">
                    Nothing arrived? Check the spam folder, then ask the team whether the address is the
                    one on the account.
                  </p>
                </div>

                <Button type="button" variant="primary" fullWidth onClick={backToSignIn}>
                  Back to sign in
                </Button>
              </div>
            ) : (
              <form onSubmit={submitForgot} className="space-y-5" noValidate>
                <div className="space-y-2">
                  <h1 className="text-3xl font-semibold text-ink">Forgotten password</h1>
                  <p className="text-base text-ink-muted">
                    Type the address you sign in with and we will email you a link to set a new one.
                  </p>
                </div>

                <Field label="Email">
                  <Input
                    type="email"
                    autoComplete="username"
                    required
                    autoFocus
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@firmadapt.com"
                  />
                </Field>

                {forgot.error ? <ErrorState error={forgot.error} title="That did not go through" /> : null}

                <Button
                  type="submit"
                  variant="primary"
                  fullWidth
                  loading={forgot.isPending}
                  disabled={email.trim() === ''}
                >
                  Email me a link
                </Button>

                <div className="text-center">
                  <Button type="button" variant="link-gray" onClick={backToSignIn}>
                    Back to sign in
                  </Button>
                </div>
              </form>
            )
          ) : mode === 'reset' ? (
            resetSubject.isPending ? (
              <p className="py-6 text-center text-base text-ink-muted">Checking that link</p>
            ) : resetSubject.error ? (
              <div className="space-y-5">
                <div className="space-y-2">
                  <h1 className="text-3xl font-semibold text-ink">That link did not work</h1>
                  <p className="text-base text-ink-muted">
                    Reset links work once and stop working after an hour. Ask for a new one and use the
                    most recent email.
                  </p>
                </div>

                <Button
                  type="button"
                  variant="primary"
                  fullWidth
                  onClick={() => {
                    setParams({}, { replace: true })
                    setMode('forgot')
                  }}
                >
                  Send me a new link
                </Button>
              </div>
            ) : (
              <form onSubmit={submitReset} className="space-y-5" noValidate>
                <div className="space-y-2">
                  <h1 className="text-3xl font-semibold text-ink">Set a new password</h1>
                  <p className="text-base text-ink-muted">
                    For {resetSubject.data?.email}. Choosing one signs the account out everywhere.
                  </p>
                </div>

                <Field
                  label="New password"
                  hint="At least twelve characters, mixing at least three of lowercase, uppercase, digits and symbols."
                >
                  <PasswordInput
                    autoComplete="new-password"
                    required
                    autoFocus
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                  />
                </Field>

                <Field
                  label="New password again"
                  error={confirmTouched && !passwordsMatch ? 'The two do not match' : undefined}
                >
                  <PasswordInput
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                  />
                </Field>

                {resetPassword.error ? (
                  <ErrorState error={resetPassword.error} title="That password was not accepted" />
                ) : null}

                <Button
                  type="submit"
                  variant="primary"
                  fullWidth
                  loading={resetPassword.isPending}
                  disabled={!passwordsMatch}
                >
                  Set password
                </Button>
              </form>
            )
          ) : challengeId === null ? (
            <form onSubmit={submitCredentials} className="space-y-5" noValidate>
              <div className="space-y-2">
                <h1 className="text-3xl font-semibold text-ink">Sign in</h1>
                <p className="text-base text-ink-muted">
                  {justReset
                    ? 'Your password is set. Sign in with it to carry on.'
                    : 'One account covers every brand. You pick which one you are working in once you are inside.'}
                </p>
              </div>

              <Field label="Email">
                <Input
                  type="email"
                  autoComplete="username"
                  required
                  autoFocus
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@firmadapt.com"
                />
              </Field>

              <Field label="Password">
                <PasswordInput
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Field>

              {login.error ? <ErrorState error={login.error} title="That did not sign you in" /> : null}

              <Button type="submit" variant="primary" fullWidth loading={login.isPending}>
                Continue
              </Button>

              <div className="text-center">
                <Button
                  type="button"
                  variant="link-gray"
                  onClick={() => {
                    setMode('forgot')
                    setPassword('')
                    setJustReset(false)
                    login.reset()
                  }}
                >
                  Forgot your password?
                </Button>
              </div>
              {/* The portal's sign-in offered its request form; it is back at /signup (2026-10-30). */}
              <p className="text-center text-sm text-ink-muted">
                New to Son of Basel?{' '}
                <a href="/signup" className="font-medium text-accent-text hover:underline">
                  Tell us what you want to build
                </a>
              </p>
            </form>
          ) : (
            <form onSubmit={submitCode} className="space-y-5" noValidate>
              <div className="space-y-2">
                <h1 className="text-3xl font-semibold text-ink">Enter your code</h1>
                <p className="text-base text-ink-muted">
                  Open your authenticator app and type the six digit code for Son of Basel.
                </p>
              </div>

              <Field label="Six digit code" hint="The code changes every thirty seconds.">
                <Input
                  ref={codeRef}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                  className="text-center text-lg tracking-[0.4em]"
                />
              </Field>

              {verifyTotp.error ? (
                <ErrorState error={verifyTotp.error} title="That code was not accepted" />
              ) : null}

              <Button
                type="submit"
                variant="primary"
                fullWidth
                loading={verifyTotp.isPending}
                disabled={code.length < 6}
              >
                Sign in
              </Button>

              <div className="text-center">
                <Button type="button" variant="link-gray" onClick={backToSignIn}>
                  Use a different account
                </Button>
              </div>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-ink-subtle">
          Internal tool. Everything you do here is recorded against your account.
        </p>
      </div>
      </div>
    </main>
  )
}
